import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Ban, Check, Copy, Eye, FileDown, Loader2, Play, QrCode, RefreshCcw, Search } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import api from "../api";
import { useLanguage } from "../i18n";
import { useAuth } from "../auth";
import { usePageMeta } from "../hooks/usePageMeta";
import AccountTabs from "../components/AccountTabs";
import { EsimCard } from "../components/EsimCard";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const PAGE_SIZE = 25;

const STATUS_STYLE = {
  active: "bg-emerald-50 text-emerald-700",
  exhausted: "bg-amber-50 text-amber-700",
  suspended: "bg-slate-100 text-slate-500",
  expired: "bg-red-50 text-red-600",
};

function UsageCell({ sim, onUsage }) {
  const [usage, setUsage] = useState(null);
  useEffect(() => {
    let on = true;
    api.get(`/esims/${sim.iccid}/usage`).then(({ data }) => { if (on) setUsage(data.data || data); }).catch(() => { if (on) setUsage({}); });
    return () => { on = false; };
  }, [sim.iccid]);
  if (usage === null) return <span className="text-xs text-slate-300">…</span>;
  if (!usage.total) return <span className="text-xs text-slate-400">—</span>;
  const pct = Math.max(0, Math.min(100, Math.round((usage.remaining / usage.total) * 100)));
  if (onUsage) onUsage(sim.iccid, pct);
  return (
    <div data-testid={`console-usage-${sim.iccid}`} className="min-w-[110px]">
      <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
        <div className={`h-full rounded-full ${pct > 20 ? "bg-[#0D9488]" : "bg-red-400"}`} style={{ width: `${pct}%` }} />
      </div>
      <div className="mt-1 font-mono-t text-[10px] text-slate-400">{pct}% · {(usage.remaining / 1024).toFixed(2)}/{(usage.total / 1024).toFixed(1)} GB</div>
    </div>
  );
}

export default function SimsConsole({ embedded = false }) {
  usePageMeta("console.title", "console.sub");
  const { t } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [sims, setSims] = useState(null);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(0);
  const [busyId, setBusyId] = useState(null);
  const [copied, setCopied] = useState(null);
  const [lowSim, setLowSim] = useState({});
  const [qrSim, setQrSim] = useState(null);
  const [detailSim, setDetailSim] = useState(null);

  const markUsage = (iccid, pct) => {
    const low = pct < 20;
    setLowSim((m) => (m[iccid] === low ? m : { ...m, [iccid]: low }));
  };

  useEffect(() => {
    if (user === null) navigate("/account");
  }, [user, navigate]);

  const load = () => api.get("/esims-console").then(({ data }) => setSims(data.data || [])).catch(() => setSims([]));
  useEffect(() => { if (user) load(); }, [user]);

  const filtered = useMemo(() => {
    const s = (sims || []);
    const needle = q.trim().toLowerCase();
    if (!needle) return s;
    return s.filter((x) => (x.iccid || "").toLowerCase().includes(needle)
      || (x.destination || "").toLowerCase().includes(needle)
      || (x.package_title || "").toLowerCase().includes(needle));
  }, [sims, q]);

  const pages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageItems = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const stats = useMemo(() => ({
    total: (sims || []).length,
    active: (sims || []).filter((s) => s.status === "active").length,
    suspended: (sims || []).filter((s) => s.status === "suspended").length,
    exhausted: (sims || []).filter((s) => s.status === "exhausted").length,
    lowdata: Object.values(lowSim).filter(Boolean).length,
  }), [sims, lowSim]);

  const act = async (fn, id) => {
    setBusyId(id);
    try {
      await fn();
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("misc.error"));
    } finally {
      setBusyId(null);
    }
  };

  const fmtDate = (iso) => (iso ? iso.slice(0, 10).split("-").reverse().join("/") : "—");

  const exportCsv = () => {
    const rows = [
      ["ICCID", t("console.package"), "Destinazione", t("console.status"), t("console.activated"), t("console.expires"), "Auto-ricarica", "Demo"],
      ...filtered.map((s) => [
        s.iccid, s.package_title || "", s.destination || "", t(`console.${s.status}`),
        fmtDate(s.created_at), fmtDate(s.expires_at), s.auto_topup ? "ON" : "OFF", s.mock ? "demo" : "",
      ]),
    ];
    const csv = rows.map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(";")).join("\n");
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `simamma-flotta-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
    toast.success(t("share.copied"));
  };
  const th = "text-left font-mono-t text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400 px-4 py-3";
  const td = "px-4 py-3.5 text-sm text-slate-700 align-middle";

  return (
    <div data-testid="sims-console" className={embedded ? "" : "px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto py-12 sm:py-16"}>
      {!embedded && (
        <>
          <AccountTabs active="console" />
          <div className="font-mono-t text-[11px] font-bold uppercase tracking-[0.3em] text-[#FF6B35] mb-3">SIMAMMA · FLEET</div>
          <h1 className="font-display font-extrabold tracking-tight text-4xl sm:text-5xl text-slate-900">{t("console.title")}</h1>
          <p className="mt-3 text-slate-600 max-w-xl">{t("console.sub")}</p>
        </>
      )}

      <div className={`${embedded ? "mt-2" : "mt-8"} grid grid-cols-2 sm:grid-cols-5 gap-4`} data-testid="console-stats">
        {[["total", stats.total], ["active", stats.active], ["suspended", stats.suspended], ["exhausted", stats.exhausted], ["lowdata", stats.lowdata]].map(([k, v]) => (
          <div key={k} className={`rounded-3xl bg-white border shadow-sm px-6 py-5 ${k === "lowdata" && v > 0 ? "border-red-300" : "border-slate-200/80"}`}>
            <div className="font-mono-t text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">{t(`console.${k}`)}</div>
            <div data-testid={`console-stat-${k}`} className={`font-display font-extrabold text-3xl mt-1 ${k === "lowdata" && v > 0 ? "text-red-600" : "text-slate-900"}`}>{v}</div>
          </div>
        ))}
      </div>

      <div className={embedded
        ? "mt-6 rounded-2xl bg-white/80 border border-slate-200/80 p-3"
        : "sticky top-[124px] z-20 mt-8 -mx-4 sm:-mx-6 lg:-mx-12 px-4 sm:px-6 lg:px-12 py-3 bg-[#FBF9F5]/90 backdrop-blur-xl border-b border-slate-200/80"}>
        <div className="flex items-center gap-3">
          <div className="flex max-w-md flex-1 items-center rounded-full border border-slate-200 bg-white shadow-sm overflow-hidden focus-within:border-[#FF6B35] transition-colors">
            <Search className="w-4 h-4 ml-5 text-slate-400 shrink-0" />
            <input data-testid="console-search" value={q} onChange={(e) => { setQ(e.target.value); setPage(0); }}
              placeholder={t("console.search")} className="flex-1 px-4 py-3 bg-transparent outline-none text-sm font-mono-t" />
          </div>
          <button data-testid="console-export-csv" onClick={exportCsv} disabled={!filtered.length}
            className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-5 py-3 font-mono-t text-[10px] font-bold uppercase tracking-widest text-slate-600 hover:border-[#FF6B35] hover:text-[#FF6B35] transition-colors disabled:opacity-40 shadow-sm">
            <FileDown className="w-3.5 h-3.5" /> {t("console.exportCsv")}
          </button>
        </div>
      </div>

      <div className="mt-6 rounded-3xl bg-white border border-slate-200/80 shadow-sm overflow-x-auto">
        {sims === null ? (
          <div className="p-10 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-[#FF6B35]" /></div>
        ) : pageItems.length === 0 ? (
          <p data-testid="console-empty" className="p-10 text-center text-sm text-slate-400">{t("console.empty")}</p>
        ) : (
          <table className="w-full min-w-[1020px]" data-testid="console-table">
            <thead className="border-b border-slate-100">
              <tr>
                <th className={th}>ICCID</th>
                <th className={th}>{t("console.package")}</th>
                <th className={th}>{t("console.status")}</th>
                <th className={th}>{t("console.data")}</th>
                <th className={th}>{t("console.activated")}</th>
                <th className={th}>{t("console.expires")}</th>
                <th className={th}>{t("console.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {pageItems.map((s) => (
                <tr key={s.iccid} data-testid={`console-row-${s.iccid}`} className="border-b border-slate-50 hover:bg-[#FAF5EE] transition-colors">
                  <td className={td}>
                    <button data-testid={`console-copy-${s.iccid}`}
                      onClick={() => { navigator.clipboard.writeText(s.iccid); setCopied(s.iccid); setTimeout(() => setCopied(null), 1500); }}
                      className="inline-flex items-center gap-2 font-mono-t text-xs font-bold text-slate-800 hover:text-[#FF6B35] transition-colors"
                      title={s.iccid}>
                      {s.iccid.slice(0, 8)}…{s.iccid.slice(-6)}
                      {copied === s.iccid ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5 text-slate-300" />}
                    </button>
                  </td>
                  <td className={td}>
                    <div className="font-medium text-slate-800">{s.package_title}</div>
                    <div className="text-xs text-slate-400">{s.destination} {s.mock && `· ${t("acc.demo")}`}</div>
                  </td>
                  <td className={td}>
                    <div className="flex items-center gap-1.5">
                      <span data-testid={`console-status-${s.iccid}`} className={`rounded-full px-3 py-1 font-mono-t text-[9px] font-bold uppercase tracking-widest ${STATUS_STYLE[s.status] || STATUS_STYLE.active}`}>
                        {t(`console.${s.status}`)}
                      </span>
                      {lowSim[s.iccid] && s.status !== "suspended" && (
                        <span data-testid={`console-low-${s.iccid}`} className="rounded-full bg-red-500 text-white px-2 py-1 font-mono-t text-[9px] font-bold uppercase tracking-widest" title={t("console.lowdataTitle")}>
                          &lt;20%
                        </span>
                      )}
                    </div>
                  </td>
                  <td className={td}><UsageCell sim={s} onUsage={markUsage} /></td>
                  <td className={`${td} text-xs font-mono-t`}>{fmtDate(s.created_at)}</td>
                  <td className={`${td} text-xs font-mono-t`}>{fmtDate(s.expires_at)}</td>
                  <td className={td}>
                    <div className="flex items-center gap-1.5">
                      <Link to="/topup" data-testid={`console-topup-${s.iccid}`}
                        className="rounded-full bg-[#FF6B35] text-white p-2 hover:bg-[#E8551F] transition-colors" title={t("console.topup")}>
                        <RefreshCcw className="w-3.5 h-3.5" />
                      </Link>
                      <button data-testid={`console-suspend-${s.iccid}`} disabled={busyId === s.iccid}
                        onClick={() => act(() => api.post(`/esims/${s.iccid}/suspend`, { suspended: !s.suspended }), s.iccid)}
                        className={`rounded-full p-2 border transition-colors ${s.suspended ? "border-emerald-300 text-emerald-600 hover:bg-emerald-50" : "border-slate-200 text-slate-400 hover:border-red-400 hover:text-red-500"}`}
                        title={s.suspended ? t("console.resume") : t("console.suspend")}>
                        {busyId === s.iccid ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : s.suspended ? <Play className="w-3.5 h-3.5" /> : <Ban className="w-3.5 h-3.5" />}
                      </button>
                      <button data-testid={`console-detail-${s.iccid}`} onClick={() => setDetailSim(s)}
                        className="rounded-full border border-slate-200 p-2 text-slate-400 hover:border-[#FF6B35] hover:text-[#FF6B35] transition-colors" title={t("console.detail")}>
                        <Eye className="w-3.5 h-3.5" />
                      </button>
                      <button data-testid={`console-qr-${s.iccid}`} onClick={() => setQrSim(s)}
                        className="rounded-full border border-slate-200 p-2 text-slate-400 hover:border-[#FF6B35] hover:text-[#FF6B35] transition-colors" title="QR">
                        <QrCode className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {pages > 1 && (
        <div className="mt-6 flex items-center justify-center gap-3" data-testid="console-pagination">
          <button data-testid="console-prev" disabled={page === 0} onClick={() => setPage((p) => p - 1)}
            className="rounded-full border border-slate-200 px-5 py-2 font-mono-t text-[10px] font-bold uppercase tracking-widest text-slate-500 hover:border-[#FF6B35] hover:text-[#FF6B35] disabled:opacity-40 transition-colors">←</button>
          <span className="font-mono-t text-xs text-slate-400">{t("console.page")} {page + 1} {t("console.of")} {pages}</span>
          <button data-testid="console-next" disabled={page >= pages - 1} onClick={() => setPage((p) => p + 1)}
            className="rounded-full border border-slate-200 px-5 py-2 font-mono-t text-[10px] font-bold uppercase tracking-widest text-slate-500 hover:border-[#FF6B35] hover:text-[#FF6B35] disabled:opacity-40 transition-colors">→</button>
        </div>
      )}

      {/* Dettaglio SIM: stessa scheda grafica di "Le mie eSIM" (uso, installa, condividi, ricarica) */}
      <Dialog open={!!detailSim} onOpenChange={(o) => { if (!o) setDetailSim(null); }}>
        <DialogContent data-testid="console-detail-dialog" className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle className="font-display">{detailSim?.package_title} · {detailSim?.destination}</DialogTitle>
          </DialogHeader>
          {detailSim && <EsimCard sim={detailSim} />}
        </DialogContent>
      </Dialog>

      {/* QR / installazione eSIM */}
      <Dialog open={!!qrSim} onOpenChange={(o) => { if (!o) setQrSim(null); }}>
        <DialogContent data-testid="console-qr-dialog" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">{t("acc.install")} — {qrSim?.package_title}</DialogTitle>
          </DialogHeader>
          <div className="text-center">
            {qrSim?.qrcode ? (
              <div className="inline-block rounded-2xl border border-slate-200 p-4 bg-white">
                <QRCodeSVG value={qrSim.qrcode} size={200} />
              </div>
            ) : (
              <p data-testid="console-qr-missing" className="py-6 text-sm text-slate-400">{t("console.noQr")}</p>
            )}
            {(qrSim?.lpa || qrSim?.matching_id) && (
              <div className="mt-6 text-left space-y-4">
                <div className="font-mono-t text-[10px] uppercase tracking-[0.25em] text-slate-400">{t("acc.manual")}</div>
                {qrSim?.lpa && (
                  <div>
                    <div className="font-mono-t text-[10px] uppercase tracking-[0.25em] text-slate-400">{t("acc.smdp")}</div>
                    <div className="font-mono-t text-sm break-all mt-1">{qrSim.lpa}</div>
                  </div>
                )}
                {qrSim?.matching_id && (
                  <div>
                    <div className="font-mono-t text-[10px] uppercase tracking-[0.25em] text-slate-400">{t("acc.code")}</div>
                    <div className="font-mono-t text-sm break-all mt-1">{qrSim.matching_id}</div>
                  </div>
                )}
                {qrSim?.direct_apple_installation_url && (
                  <a data-testid="console-apple-install" href={qrSim.direct_apple_installation_url}
                    className="block text-center rounded-full bg-[#1E293B] text-white px-6 py-3 font-mono-t text-[11px] uppercase tracking-[0.2em] hover:bg-[#FF6B35] transition-colors">
                    iOS 17.4+ Direct Install
                  </a>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
